// СЛЕПОК РАЗБОРА ДЛЯ ССЫЛКИ ИЗ ПИСЬМА — общее для клиента и сервера. Supabase, часть 1
// (`34a19875cbb881b5bbbac2a4c7be7840`): `s490.d1` (слепок = выход System 1 + System 2 + ползунки + имена и
// даты этапов, без сырых ответов, версия схемы с первого дня), `s490.d2` (12 мес, продление при открытии).
//
// ⭐ СЛЕПОК = ПАСПОРТ ОКНА OPEN КАК ЕСТЬ + ДАТА ДОКУМЕНТА. Шаг 7 строит паспорт документа
// (`documentPassport.js`): этапы, ползунки и кнопки шага 3 (BG1–BG4, свой ответ BG3 — строкой, `s987.d4`),
// БЕЗ `text` (T1–T3 — сырьё, `s490.d1`), и ровно его рисует Open. До cc-213 кнопок в слепке не было вовсе.
// Выход движка замораживается в слепке: правка детектора потом не перепишет уже отправленный разбор.
// С строки 73 паспорт несёт и `system2` (вырезки судьи цитат) — ключи проверяются строгим списком.
//
// ⭐ ТОКЕН РОЖДАЕТ КЛИЕНТ (`crypto.getRandomValues`, 256 бит), СЕРВЕР ХРАНИТ ТОЛЬКО ЕГО sha256. Так
// повторный тап того же документа несёт тот же токен и строки не плодит (уникальность `token_hash`),
// а серверу не нужен ни второй секрет, ни хранение токена в открытую. Форма токена проверяется
// `TOKEN_RE`; слабый токен клиент может сделать только себе же.

import { BUTTON_GROUPS, BUTTON_GROUP_KEYS, LIVING_FOR_MAX } from '../contracts.js';

/** Версия схемы слепка (`schema_version` строки). Меняется формат `snapshot` — поднимать. */
export const SNAPSHOT_SCHEMA_VERSION = 1;

/** Токен — 32 случайных байта в base64url без `=`: ровно 43 знака. */
export const TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

/** Путь страницы разбора по ссылке (`/r/{token}`) и адрес функции слепка. */
export const READING_PREFIX = '/r/';
export const SNAPSHOT_PATH = '/api/snapshot';

/** Потолок слепка, байт JSON. Замер шага 0: 2,0–2,8 КБ на 8 прогонах; с запасом на имена этапов. */
export const SNAPSHOT_MAX_BYTES = 32 * 1024;

/** Ключи паспорта, которые нужны окну Open (`buildPassport`), — и никаких других. */
const PASSPORT_KEYS = new Set(['input', 'system1', 'carrierStages', 'system2', 'silhouette', 'stageCount']);
/**
 * Из ввода — ползунки, имена/даты этапов и выборы кнопок. `text` (T1–T3) — свободные ответы, нельзя.
 * `buttons` — часть документа (§II/§IV печатают их дословно, `s987.d4`), но строгой формой: ряд на этап,
 * ключи ровно BG1–BG4, в группе не больше `BUTTON_MAX_PICKS` строк, строка не длиннее `LIVING_FOR_MAX`
 * (длиннее не бывает ни у одного чипа, а свой ответ BG3 режет ввод). Так длинный свободный текст не
 * уедет на сервер под видом кнопки.
 */
const WIZARD_KEYS = new Set(['sliders', 'stages', 'buttons']);

/** Потолок строк в группе: больше, чем вариантов у самой длинной группы, не бывает. */
export const BUTTON_MAX_PICKS = Math.max(...Object.values(BUTTON_GROUPS).map((g) => g.options.length + (g.custom ? 1 : 0)));

function buttonsProblem(buttons, n) {
  if (!Array.isArray(buttons) || buttons.length !== n) return 'bad_buttons';
  for (const row of buttons) {
    if (!isObj(row)) return 'bad_buttons';
    const keys = Object.keys(row);
    if (keys.length !== BUTTON_GROUP_KEYS.length || !BUTTON_GROUP_KEYS.every((k) => k in row)) return 'bad_buttons';
    for (const k of BUTTON_GROUP_KEYS) {
      const picks = row[k];
      if (!Array.isArray(picks) || picks.length > BUTTON_MAX_PICKS) return 'bad_buttons';
      if (picks.some((t) => typeof t !== 'string' || t.length > LIVING_FOR_MAX)) return 'bad_buttons';
    }
  }
  return null;
}

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * `system2` — выход `selectQuotes` (строка 73, `s987.d3`): вырезки судьи, прошедшие verbatim, а не ответы
 * целиком (`s490.d1`). Ключи — строгим списком, как у паспорта: сырьё не уедет под видом нового поля System 2.
 */
const SYSTEM2_KEYS = new Set(['perStage', 'transport', 'sectionIV', 'sectionVI', 'sectionVII']);
const PER_STAGE_KEYS = new Set(['stage', 'isCarrier', 'field', 'quote', 'reason', 'no_quote']);

function system2Problem(s2) {
  if (s2 === null || s2 === undefined) return null;
  if (!isObj(s2) || Object.keys(s2).some((k) => !SYSTEM2_KEYS.has(k))) return 'bad_system2';
  if ('perStage' in s2) {
    if (!Array.isArray(s2.perStage)) return 'bad_system2';
    if (s2.perStage.some((r) => !isObj(r) || Object.keys(r).some((k) => !PER_STAGE_KEYS.has(k)))) return 'bad_system2';
  }
  return null;
}

/** Новый токен. `cryptoImpl` подставляется тестом. */
export function newToken(cryptoImpl = globalThis.crypto) {
  const bytes = new Uint8Array(32);
  cryptoImpl.getRandomValues(bytes);
  let bin = '';
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Токен из пути `/r/{token}` (с косой на конце — тот же адрес) или `null`. */
export function tokenFromPath(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith(READING_PREFIX)) return null;
  const t = pathname.slice(READING_PREFIX.length).replace(/\/$/, '');
  return TOKEN_RE.test(t) ? t : null;
}

/** Слепок из паспорта шага 7 и даты документа. */
export function makeSnapshot(passport, generatedAt) {
  return { generatedAt, passport };
}

/**
 * Проверка слепка перед записью. Строгий список ключей, а не поиск запрещённых: новое поле ввода
 * визарда не уедет на сервер, пока его сюда не впишут руками.
 *
 * @returns {string|null} код отказа или `null`, если слепок годен
 */
export function snapshotProblem(snapshot) {
  if (!isObj(snapshot)) return 'not_object';
  const keys = Object.keys(snapshot);
  if (keys.length !== 2 || !('generatedAt' in snapshot) || !('passport' in snapshot)) return 'bad_shape';
  if (typeof snapshot.generatedAt !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(snapshot.generatedAt)) return 'bad_date';
  const p = snapshot.passport;
  if (!isObj(p)) return 'bad_passport';
  if (Object.keys(p).some((k) => !PASSPORT_KEYS.has(k))) return 'extra_passport_key';
  const s2p = system2Problem(p.system2);
  if (s2p) return s2p;
  if (!isObj(p.input) || Object.keys(p.input).length !== 1 || !isObj(p.input.wizard)) return 'bad_input';
  if (Object.keys(p.input.wizard).some((k) => !WIZARD_KEYS.has(k))) return 'free_answers';
  if (!Array.isArray(p.input.wizard.sliders)) return 'bad_sliders';
  if ('buttons' in p.input.wizard) {
    const bp = buttonsProblem(p.input.wizard.buttons, p.input.wizard.sliders.length);
    if (bp) return bp;
  }
  let bytes;
  try { bytes = new TextEncoder().encode(JSON.stringify(snapshot)).length; } catch { return 'not_json'; }
  if (bytes > SNAPSHOT_MAX_BYTES) return 'too_large';
  return null;
}

/**
 * JSON с ключами по алфавиту на всех уровнях. Postgres `jsonb` порядок ключей не хранит, поэтому
 * «тот же ли это слепок» сравнивается в этой форме, а не по `JSON.stringify`.
 */
export function canonicalJson(v) {
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(',')}]`;
  if (isObj(v)) {
    return `{${Object.keys(v).sort().filter((k) => v[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${canonicalJson(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v === undefined ? null : v);
}
